// The collection: everything you dig up, catch, or find across Toast Town. This
// is the shared item registry + found-tracking that feeds the Museum (and later
// the Aquarium). Dig spots call `addFound(id)`; the Museum reads the registry to
// lay out exhibits and pays crumbs as they fill.
//
// Add an item by appending to ITEMS. `kind` groups exhibits; `rarity` drives
// value + display order. Keep it wholesome and bread-world flavored.

import { getCollection, save } from './save.js';

// Event bus for collection first-finds (HUD shows achievement toasts).
const listeners = new Set();
export function onCollectionEvent(fn){ listeners.add(fn); return () => listeners.delete(fn); }
function emit(id, first){ listeners.forEach(fn => fn({ id, first })); }

// rarity → crumb value when an exhibit slot is first filled
export const RARITY_VALUE = { common: 5, uncommon: 12, rare: 30, legendary: 75 };

export const ITEMS = {
  // --- fossils (the Museum's Paleo Hall) ---
  'fossil-trilobite': { name: 'Trilobite Crumb',   kind: 'fossil',   rarity: 'common',    icon: '🐚' },
  'fossil-fishbone':  { name: 'Ancient Fishbone',  kind: 'fossil',   rarity: 'common',    icon: '🐟' },
  'fossil-fern':      { name: 'Fossil Fern',       kind: 'fossil',   rarity: 'uncommon',  icon: '🌿' },
  'fossil-ammonite':  { name: 'Spiral Ammonite',   kind: 'fossil',   rarity: 'uncommon',  icon: '🐌' },
  'fossil-tooth':     { name: 'Giant Loaf Tooth',  kind: 'fossil',   rarity: 'rare',      icon: '🦷' },
  'fossil-skull':     { name: 'Doughsaur Skull',   kind: 'fossil',   rarity: 'legendary', icon: '💀' },
  // --- artifacts (the Antiquities Hall) ---
  'relic-coin':       { name: 'Old Crumb Coin',    kind: 'artifact', rarity: 'common',    icon: '🪙' },
  'relic-pot':        { name: 'Cracked Clay Pot',  kind: 'artifact', rarity: 'uncommon',  icon: '🏺' },
  'relic-ring':       { name: 'Toasty Signet Ring',kind: 'artifact', rarity: 'rare',      icon: '💍' },
  'relic-crown':      { name: "Baker King's Crown", kind: 'artifact', rarity: 'legendary', icon: '👑' },
  'relic-gauntlet':   { name: 'Baker King\'s Gauntlet', kind: 'artifact', rarity: 'rare',      icon: '🥊' },
  'relic-scepter':    { name: 'Baker King\'s Scepter',  kind: 'artifact', rarity: 'legendary', icon: '⚜️' },
  'lore-book':        { name: 'Crust, Toast & Jam',     kind: 'artifact', rarity: 'uncommon',  icon: '📖' },
  'relic-bone':       { name: 'Baker King\'s Bread-Bone',kind: 'artifact', rarity: 'rare',     icon: '🦴' },
  'ghost-lamp':       { name: 'Moldy Moe\'s Ghost-Lamp', kind: 'artifact', rarity: 'uncommon',  icon: '🔦' },
  'lunar-stabilizer': { name: 'Lunar Stabilizer',       kind: 'artifact', rarity: 'rare',        icon: '⚙️' },
  'relic-quadra-valve': { name: 'Mini Quadra-Valve',    kind: 'artifact', rarity: 'rare',        icon: '⚙️' },
  // --- junk (still fun to find; low value) ---
  'junk-cap':         { name: 'Bottle Cap',        kind: 'junk',     rarity: 'common',    icon: '🔵' },
  'junk-boot':        { name: 'Soggy Boot',        kind: 'junk',     rarity: 'common',    icon: '🥾' },
  // --- fish (the Aquarium's tanks) ---
  'fish-toastfish':   { name: 'Toastfish',         kind: 'fish',     rarity: 'common',    icon: '🐟' },
  'fish-breadbass':   { name: 'Bread Bass',        kind: 'fish',     rarity: 'common',    icon: '🐠' },
  'fish-crumbsalmon': { name: 'Crumb Salmon',      kind: 'fish',     rarity: 'uncommon',  icon: '🐟' },
  'fish-jellyjelly':  { name: 'Jelly Jelly',       kind: 'fish',     rarity: 'uncommon',  icon: '🪼' },
  'fish-lobsterroll': { name: 'Lobster Roll',      kind: 'fish',     rarity: 'rare',      icon: '🦞' },
  'fish-goldencrumb': { name: 'Golden Crumbfish',  kind: 'fish',     rarity: 'rare',      icon: '🐡' },
  'fish-whalebread':  { name: 'Whalebread',        kind: 'fish',     rarity: 'legendary', icon: '🐋' },
  'fish-bootfish':    { name: 'Bootfish',          kind: 'fish',     rarity: 'common',    icon: '🥾' }
};

// ordered kinds for exhibit layout
export const KINDS = ['fossil', 'artifact', 'junk', 'fish'];

export function foundMap(){ return getCollection().found; }
export function hasFound(id){ return (foundMap()[id] || 0) > 0; }
export function foundCount(id){ return foundMap()[id] || 0; }

// Record a find; returns the new count for that item.
export function addFound(id, n = 1){
  if (!ITEMS[id]) { console.warn('unknown collectible', id); return 0; }
  const f = foundMap();
  f[id] = (f[id] || 0) + n;
  save();
  return f[id];
}

// A random collectible id, weighted toward common. Handy for dig-spot payouts.
export function randomFind(){
  const weights = { common: 60, uncommon: 28, rare: 10, legendary: 2 };
  const pool = [];
  for (const id in ITEMS) for (let i = 0; i < weights[ITEMS[id].rarity]; i++) pool.push(id);
  return pool[(Math.random() * pool.length) | 0];
}

// Progress summary for the Museum HUD: found/total overall and per kind.
export function collectionStats(){
  const ids = Object.keys(ITEMS);
  const foundIds = ids.filter(hasFound);
  const byKind = {};
  for (const k of KINDS){
    const kIds = ids.filter(id => ITEMS[id].kind === k);
    byKind[k] = { found: kIds.filter(hasFound).length, total: kIds.length };
  }
  return { found: foundIds.length, total: ids.length, byKind };
}
